/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import dayjs from 'dayjs';

import { environment } from 'environments/environment';
import { Constants } from '@shared/models/constants.model';
import { InviteDTO } from '@shared/models/invite.model';
import { ServerResponseModel } from '@shared/models/serverResponse.model';
import { HttpHelpersService } from './http-helpers.service';
import { StorageService } from './storage.service';
import { UtilsService } from './utils.service';
import { BraintreeService } from './braintree.service';
import { BreakdownService } from './breakdown.service';
import { EmailsService } from './emails.service';
import { SignupService } from './signup.service';
import { InviteService } from './invite.service';

@Injectable({ providedIn: 'root' })
export class PurchaseService {
  constructor(
    private braintreeService: BraintreeService,
    private breakdownService: BreakdownService,
    private emailService: EmailsService,
    private inviteService: InviteService,
    private http: HttpClient,
    private signupService: SignupService,
    private storageService: StorageService,
    private utilsService: UtilsService
  ) {}

  createPurchase(purchaseData: any) {
    return HttpHelpersService.handleGetResponse(
      this.http.post(`${environment.apiHostname}/v1/purchases`, purchaseData)
    );
  }

  createOrderWithPurchases(payload: { order: any; purchases: any[] }) {
    return HttpHelpersService.handleGetResponse(
      this.http.post(`${environment.apiHostname}/v1/orders/with-purchases`, payload)
    );
  }

  updatePurchase(purchaseData: any) {
    return HttpHelpersService.handleOperationResponse(
      this.http.put(`${environment.apiHostname}/v1/purchases/${purchaseData.id}`, purchaseData)
    );
  }

  getPurchase(purchaseData: any) {
    return HttpHelpersService.handleGetResponse(
      this.http.get(`${environment.apiHostname}/v1/purchases/${purchaseData.id}`)
    );
  }

  // get the subtotal for all of the items in the cart
  getSubtotal(cartItems: any[]) {
    if (!cartItems || !cartItems.length) {
      return 0;
    }

    return cartItems.reduce((acc, item) => {
      return acc + parseFloat(item.price) * parseInt(item.quantity || 1, 10);
    }, 0);
  }

  prepareEventPurchaseOptions(cartItems, eventData) {
    return cartItems.map((option) => {
      const newOption = {
        ...option,
        eventId: eventData.id,
        sportId: eventData.sport.id,
        sport: eventData.sport,
        event: eventData,
      };
      if (option.type === Constants.PURCHASE_TYPE.ONE_EVENT_GF) {
        newOption.price = eventData.price.gameFilm.value || eventData.price.gameFilm.valueStriked;
      }
      if (option.type === Constants.PURCHASE_TYPE.ONE_EVENT_GFHR) {
        newOption.price = eventData.price.gameFilmPlusHR.value || eventData.price.gameFilmPlusHR.valueStriked;
      }
      if (option.type === Constants.PURCHASE_TYPE.ONE_EVENT_GFHRP) {
        newOption.price = eventData.price.gameFilmHRPro.value || eventData.price.gameFilmHRPro.valueStriked;
      }
      if (option.type === Constants.PURCHASE_TYPE.ONE_EVENT_TP) {
        newOption.price = eventData.price.teamPackage.value || eventData.price.teamPackage.valueStriked;
      }
      if (option.type === Constants.PURCHASE_TYPE.HR) {
        newOption.price = eventData.price.highlightReel.value || eventData.price.highlightReel.valueStriked;
        newOption.includeGF = eventData.price.highlightReel.includeGF;
      }
      if (option.type === Constants.PURCHASE_TYPE.PRO_HR) {
        newOption.price = eventData.price.proHighlightReel.value || eventData.price.proHighlightReel.valueStriked;
        newOption.includeGF = eventData.price.proHighlightReel.includeGF;
      }
      if (option.type === Constants.PURCHASE_TYPE.COMPLETE_GP) {
        newOption.price = eventData.price.completeGamePackage.value || eventData.price.completeGamePackage.valueStriked;
      }
      if (option.type === Constants.PURCHASE_TYPE.PERFECT_GP) {
        newOption.price = eventData.price.perfectGamePackage.value || eventData.price.perfectGamePackage.valueStriked;
      }

      return newOption;
    });
  }

  getPlan() {
    const savedPlan = this.storageService.getLocalStorageItem('plan');
    if (savedPlan) {
      try {
        return JSON.parse(savedPlan);
      } catch (err) {
        return null;
      }
    }
    return null;
  }

  setPlan(plan) {
    this.storageService.setLocalStorageItem('plan', JSON.stringify(plan));
  }

  setReturnUrl(returnUrl) {
    const plan = JSON.parse(this.storageService.getLocalStorageItem('plan')) || {};
    plan.returnUrl = returnUrl;
    this.storageService.setLocalStorageItem('plan', JSON.stringify(plan));
  }

  resetPlan() {
    this.storageService.deleteLocalStorageItem('plan');
  }

  roundPrices(plan) {
    if (!plan) {
      return;
    }
    if (plan.totalPrice) {
      return parseFloat(plan.totalPrice.toFixed(2));
    }

    return 0.0;
  }

  createTransaction(username, amount, token?: string, saveCard = true): Observable<any> {
    if (amount === 0) {
      return of(null);
    }

    return this.braintreeService.createTransaction(username, amount, token, saveCard).pipe(
      map((r) => {
        this.utilsService.logToServer(
          Constants.LOG_TYPES.TRANSACTION,
          { username },
          { username, amount, tokenPresent: !!token, saveCard },
          { transaction_id: r?.transaction?.id, status: r?.transaction?.status, amount: r?.transaction?.amount }
        );

        if (r.success) {
          return r.transaction;
        }

        throw new Error(r.message);
      })
    );
  }

  purchaseAndCreateOrder(transaction, user, order, purchases) {
    const orderPayload = {
      user: { id: user.id, type: user.type },
      /* generate a random transactionId where the order is given for free */
      braintreeTransactionId: transaction ? transaction.id : `empty_${this.utilsService.generateRandomString(6)}`,
      processingFee: order.processingFee,
      coupon: order.coupon,
      total: order.total,
    };

    const createPayload = {
      order: orderPayload,
      purchases,
    };

    this.utilsService.logToServer(Constants.LOG_TYPES.CREATE_ORDER, orderPayload.user, createPayload);

    return this.createOrderWithPurchases(createPayload).pipe(
      catchError((error) => {
        this.utilsService.logToServer(Constants.LOG_TYPES.CREATE_ORDER, orderPayload.user, createPayload, error);
        throw error;
      }),
      tap((createdResults) => {
        // log result to server
        this.utilsService.logToServer(Constants.LOG_TYPES.CREATE_ORDER, orderPayload.user, createdResults);

        // send Receipt Email
        this.sendReceiptEmail(transaction, createdResults.order.orderNo, user.email, purchases, createdResults?.order?.id).subscribe();

        // send emails related to this order (GF ready)
        this.sendOrderRelatedEmails(createdResults).subscribe();
      }),
      // create breakdown request
      switchMap((createdResults) =>
        this.createBreakdownRequests(createdResults.purchases).pipe(map(() => createdResults))
      ),
      // send Invites
      switchMap((createdResults) => {
        const purchasesWithInvites = purchases.filter((purchase) => purchase?.inviteeEmail);

        // if no invites to send then resolve
        if (!purchasesWithInvites?.length) {
          return of(null);
        }

        const inviteReqs = [];

        // Game Film Invites
        if (purchasesWithInvites?.length) {
          inviteReqs.push(
            ...purchasesWithInvites.map((purchase) => {
              const purchaseResponse = createdResults.purchases.find(
                (p) => p.type === purchase.type && p.metadata.tempId === purchase.tempId
              );
              return this.sendInvite(
                { ...purchase, purchase: purchaseResponse },
                orderPayload.user.id,
                orderPayload.user.type
              );
            })
          );
        }

        return forkJoin(inviteReqs);
      })
    );
  }

  getProductsPricing() {
    return HttpHelpersService.handleGetResponse(this.http.get(`${environment.apiHostname}/v1/pricing`));
  }

  private sendOrderRelatedEmails(createdResults): Observable<any> {
    this.utilsService.logToServer(Constants.LOG_TYPES.SEND_ORDER_RELATED, createdResults.order.user, null);
    return this.emailService.sendOrderRelatedEmails(createdResults.order.id).pipe(
      catchError((err) => {
        this.utilsService.logToServer(Constants.LOG_TYPES.SEND_ORDER_RELATED, createdResults.order.user, null, err);
        return of(null);
      })
    );
  }

  private sendReceiptEmail(transaction: any, orderNo: string, email: string, purchases: any[], orderId?: number) {
    /* send receipt on email */
    const receiptData = {
      emailTo: email,
      paymentMethod: transaction ? `${transaction.creditCard.cardType} - ${transaction.creditCard.last4}` : '-',
      amountPaid: transaction ? transaction.amount : '0.00',
      orderNo,
      orderId,
      datePaid: dayjs().format('MMMM D, YYYY'),
    };
    const receiptItems = [];
    purchases.forEach((option) => {
      const existingOption = receiptItems.find((item) => {
        return (
          option.type === item.type &&
          (this.isHrItem(option) ||
            (!this.isHrItem(option) && item.event && option.event && item.event.id === option.event.id))
        );
      });
      if (existingOption) {
        existingOption.quantity++;
      } else {
        receiptItems.push({ quantity: 1, ...option });
      }
    });

    receiptItems.forEach((item, index) => {
      receiptData[`product${index + 1}item`] = Constants.PURCHASE_TYPE_NAME[item.type];
      receiptData[`product${index + 1}event`] = !item.event || this.isHrItem(item) ? '-' : item.event.name;
      receiptData[`product${index + 1}quantity`] = item.quantity;
    });

    return this.emailService.sendReceiptEmail(receiptData);
  }

  private isHrItem(option) {
    return (
      option &&
      option.type &&
      (option.type === Constants.PURCHASE_TYPE.HR || option.type === Constants.PURCHASE_TYPE.PRO_HR)
    );
  }

  private sendInvite(cartItem: any, userId: number, userType?: string): Observable<any> {
    if (!cartItem || !cartItem.inviteeEmail) {
      return of(null);
    }

    let firstName = '';
    let lastNames = [''];
    if (cartItem.inviteeFullName) {
      [firstName, ...lastNames] = cartItem.inviteeFullName.split(' ');
    }

    const invite = {
      inviteeEmail: cartItem.inviteeEmail,
      user: { id: userId },
      purchase: { id: cartItem.purchase.id },
      inviteeSportId: cartItem.sportId,
      isTeamInvite: cartItem.purchase.type === Constants.PURCHASE_TYPE.ONE_EVENT_TP,
      isTPAthlete:
        cartItem.purchase.type === Constants.PURCHASE_TYPE.ONE_EVENT_TP && userType === Constants.USER_TYPE.PARENT,
      toUser: undefined,
    };

    return this.signupService
      .prepareInviteeAccount(
        {
          ...cartItem.invitee,
          firstName,
          lastName: lastNames.join(' '),
          email: cartItem.inviteeEmail,
          userDetails: { graduationYear: cartItem.inviteeGradYear },
        },
        cartItem.purchase.type,
        invite.isTPAthlete
      )
      .pipe(
        mergeMap((result: ServerResponseModel) => {
          // if user exists then set invite to user
          if (result && result.data) {
            invite.toUser = {
              id: result.data.id,
            };
          }

          // if user doesn't exist then continue
          return this.inviteService.addInvite(invite);
        }),
        catchError((err) => {
          this.utilsService.logToServer(Constants.LOG_TYPES.ADD_INVITES, invite.user, invite, err);
          return of(null);
        }),
        tap((newInvite: InviteDTO | null) => {
          if (!newInvite || !newInvite.id) {
            return;
          }

          this.utilsService.logToServer(Constants.LOG_TYPES.ADD_INVITES, newInvite.user, newInvite);
          // if there is no user for this email send the invite email
          this.inviteService
            .sendInviteEmail(invite.inviteeEmail, newInvite.id)
            .pipe(
              catchError((err) => {
                this.utilsService.logToServer(
                  Constants.LOG_TYPES.SEND_INVITES,
                  invite.user,
                  {
                    email: invite.inviteeEmail,
                    id: newInvite.id,
                    existingUser: !!newInvite.toUser,
                  },
                  err
                );
                return of(null);
              })
            )
            .subscribe((result) => {
              if (result) {
                this.utilsService.logToServer(Constants.LOG_TYPES.SEND_INVITES, newInvite.user, result);
              }
            });
        })
      );
  }

  private createBreakdownRequests(purchases) {
    const purchaseIds = purchases
      .filter(
        (purchase) =>
          purchase.type === Constants.PURCHASE_TYPE.BREAKDOWN ||
          purchase.type === Constants.PURCHASE_TYPE.BREAKDOWN_ONE_GAME ||
          purchase.type === Constants.PURCHASE_TYPE.BREAKDOWN_ONE_GAME_TP
      )
      .map((purchase) => purchase.id);

    if (purchaseIds?.length) {
      return this.breakdownService.addBreakdownRequest(purchaseIds);
    }
    return of(purchases);
  }
}
